import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import type { Database } from "@/integrations/supabase/types";

type UserRole = {
  role_id: string;
  role: Database["public"]["Tables"]["role"]["Row"] | null;
};

type UserProfile = Database["public"]["Tables"]["user_account"]["Row"] & {
  user_role: UserRole[];
};

export function useUserProfile() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user-profile", user?.id],
    queryFn: async () => {
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("user_account")
        .select(`
          *,
          user_role(
            role_id,
            role:role_id(*)
          )
        `)
        .eq("external_auth_user", user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

/**
 * Whether the current user has the 'admin' role. Single source of truth for
 * this check -- server-side RLS is the real gate, this is only for hiding/
 * disabling UI affordances the user couldn't act on anyway.
 */
export function useIsAdmin(): boolean {
  const { data: profile } = useUserProfile();
  return !!(profile as UserProfile | undefined)?.user_role?.some((ur) => ur.role?.name === "admin");
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (updates: { display_name?: string; avatar_url?: string }) => {
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("user_account")
        .update(updates)
        .eq("external_auth_user", user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-profile"] });
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating profile",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useUploadAvatar() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const updateProfile = useUpdateProfile();

  return useMutation({
    mutationFn: async (file: File) => {
      if (!user) throw new Error("Not authenticated");

      const fileExt = file.name.split(".").pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from("avatars").getPublicUrl(fileName);

      return data.publicUrl;
    },
    onSuccess: (publicUrl) => {
      updateProfile.mutate({ avatar_url: publicUrl });
    },
    onError: (error: Error) => {
      toast({
        title: "Error uploading avatar",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
